import { Divider, notification } from 'antd';
import cx from 'classnames';
import React, { useEffect } from 'react';
import { hasFlag } from '../../Helper';
import { GraphicActionType, GraphicMessage, GraphicPageMode, GraphicSyncMessage } from '../../model/GraphicModel';
import { useStoreDispatch, useStoreSelector } from '../../store';
import { setGraphicActionType, setGraphicAlarmPermissions } from '../../store/graphic/actions';
import { errorSelector, graphicActionTypeSelector, graphicAlarmPermissionEventsSelector, mapModeSelector } from '../../store/graphic/selectors';
import { HeaderTitleBar, Modal } from '../common';
import { GraphicDirectory } from '../graphic';
import { MapDesigner } from './MapDesigner/MapDesigner';
import { MapDirectoryIcon, MapDirectoryNoFolderIcon, WarningSign } from './MenuBar/Icons';
import styles from './graphic.module.scss';
import { getBaseGraphicAlarmPermission } from './helper';

//Avoid creating object style inline, since increases reconciliations
const maskStyle: React.CSSProperties = { backgroundColor: '#dcdcdcb8' };
const warningStyle: React.CSSProperties = { width: '30px', height: '27px' };

const footer: React.ReactNode = (
	<div key='warning' className={styles.warningMessage}>
		<div>
			<div className={styles.warningIcon}>
				<WarningSign elementStyle={warningStyle} />
			</div>
			<span className={styles.warningText}>
				{_('MapDirectoryWarning_1')}
				<br />
				{_('MapDirectoryWarning_2')}
			</span>
		</div>
	</div>
);

const Graphic: React.FC = () => {
	//#region state management variables
	const dispatch = useStoreDispatch();
	const error = useStoreSelector(errorSelector);

	const mapMode: GraphicPageMode = useStoreSelector<GraphicPageMode>(mapModeSelector);
	const graphicActionType: GraphicActionType = useStoreSelector<GraphicActionType>(graphicActionTypeSelector);
	const graphicMessages: GraphicMessage[] = useStoreSelector<GraphicMessage[]>(graphicAlarmPermissionEventsSelector);
	//#endregion

	useEffect(() => {
		if (graphicMessages && graphicMessages.length > 0) {
			const latestMessage: GraphicSyncMessage = graphicMessages.pop().SyncMessage;
			dispatch(setGraphicAlarmPermissions(latestMessage.AlarmPermissions));
		}
	}, [graphicMessages]);

	useEffect(() => {
		if (error) {
			notification['error']({
				message: error,
			});
		}
	}, [error]);

	useEffect(() => {
		dispatch(setGraphicAlarmPermissions(getBaseGraphicAlarmPermission()));
	}, []);

	let canvasContent: React.ReactNode = <></>;
	if (hasFlag(graphicActionType, GraphicActionType.Canvas)) {
		canvasContent = (
			<>
				<MapDesigner />
			</>
		);
	}

	const mapDirectoryContent: React.ReactNode[] = [];
	if (hasFlag(graphicActionType, GraphicActionType.Directory)) {
		mapDirectoryContent.push(
			<React.Fragment key='directory'>
				<GraphicDirectory isModal={false} />
				<Divider />
				{footer}
			</React.Fragment>
		);
	}

	if (hasFlag(graphicActionType, GraphicActionType.ModalDirectory)) {
		mapDirectoryContent.push(
			<React.Fragment key='modal-directory'>
				<Modal
					keyboard={false}
					maskClosable={false}
					closable={true}
					onCancel={() => {
						dispatch(setGraphicActionType(graphicActionType & ~GraphicActionType.ModalDirectory));
					}}
					onClickOk={() => null}
					width={1000}
					title={
						<span className={styles.mapDirectoryTitle}>
							<MapDirectoryIcon />
							<span className={cx(styles.mapDirectoryTitleText, styles.mapDirectoryTitleTextModal)}>{_('MapDirectory')}</span>
						</span>
					}
					visible
					customZoomClass={styles.graphicsModalDisplayContainer}
					className={cx(styles.graphicsModalContainer, { [styles.graphicsModalDisplayContainer]: false })}
					maskStyle={maskStyle}
					footer={[footer]}
					closeIconStyle={{ color: 'white' }}>
					<GraphicDirectory isModal={true} />
				</Modal>
			</React.Fragment>
		);
	}

	return (
		<>
			{hasFlag(graphicActionType, GraphicActionType.Canvas) ? (
				<HeaderTitleBar
					title={mapMode === GraphicPageMode.Design ? _('GraphicsEditMode') : _('GraphicsLiveMode')}
					icon={<MapDirectoryNoFolderIcon />}
				/>
			) : (
				<HeaderTitleBar title={_('MapStudio')} icon={<MapDirectoryNoFolderIcon />} />
			)}
			<div id='graphicComponentContainer' className={styles.graphicComponentContainer}>
				{canvasContent}
				{mapDirectoryContent}
			</div>
		</>
	);
};

export { Graphic };
